import apiClient from './apiClient';
import type {
  Client,
  ClientClinician,
  ClientStatus,
  CreateClientDto,
  UpdateClientDto,
  ClientListResponse,
} from '../../types/clients';

// ============================================================================
// Status Update Types
// ============================================================================

export interface BulkUpdateClientStatusResult {
  updated: number;
}

// ============================================================================
// Caseload Transfer Types
// ============================================================================

/**
 * Disposition for a client's open Red Dots when their Overseeing BCBA changes.
 * TRANSFER_TO_NEW moves them to the incoming BCBA (default), KEEP_WITH_OUTGOING
 * leaves them with the outgoing BCBA, UNASSIGN routes them to Org Admins.
 */
export type RedDotDisposition = 'TRANSFER_TO_NEW' | 'KEEP_WITH_OUTGOING' | 'UNASSIGN';

export interface RedDotDispositionResult {
  disposition: RedDotDisposition;
  movedCount: number;
  unassignedCount: number;
}

export interface TransferCaseloadRequest {
  fromMemberId: string;
  toMemberId: string;
  clientIds?: string[];
  redDotDisposition?: RedDotDisposition;
}

export interface TransferCaseloadResponse {
  transferred: number;
  fromMemberId: string;
  toMemberId: string;
  redDots?: RedDotDispositionResult;
}

export interface TransferCaseloadPreviewClient {
  clientId: string;
  firstName: string;
  lastName: string;
  count: number;
}

export interface TransferCaseloadPreviewResult {
  clientCount: number;
  openRedDotCount: number;
  clientsWithOpenRedDots: TransferCaseloadPreviewClient[];
}

// ============================================================================
// Bulk Upload Types
// ============================================================================

export interface BulkClientValidateRow {
  row: number;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  center?: string;
  centerId?: string;
  valid: boolean;
  reason?: string;
}

export interface BulkClientValidateResponse {
  rows: BulkClientValidateRow[];
  validCount: number;
  invalidCount: number;
  totalCount: number;
}

export interface BulkClientExecuteResponse {
  created: number;
  skipped: number;
}

export interface GetClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  centerId?: string;
  assigneeId?: string;
  /** Filter to clients connected to this clinician (OrganizationMember id). */
  clinicianMemberId?: string;
  /** Filter to clients whose Overseeing BCBA is this clinician (OrganizationMember id). */
  overseeingBcbaMemberId?: string;
  /** Only clients a new workflow may be started on — excludes Discharged and Inactive (ADO #573). */
  workflowEligible?: boolean;
}

// ============================================================================
// Client–Provider Assignment + Coverage Types
// ============================================================================

export interface AssignmentProvider {
  id: string;
  userId: string;
  user: { id: string; firstName: string; lastName: string; email: string };
}

export interface ProviderAssignment {
  id: string;
  clientId: string;
  providerMemberId: string;
  relationshipType: 'PROVIDER' | 'COVERAGE' | string;
  effectiveFrom: string;
  effectiveTo: string | null;
  assignedById: string | null;
  provider?: AssignmentProvider;
}

export type CoverageWindow = ProviderAssignment;

export interface BulkAssignProvidersPayload {
  clientIds: string[];
  providerMemberId: string;
}

export interface BulkAssignProvidersResult {
  created: number;
  skipped: number;
  assignmentIds: string[];
}

export interface SetCoveragePayload {
  coveringMemberId: string;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface AssignableProvider {
  memberId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  centerName: string | null;
  /** Primary role name for display (first role, alphabetical). Null when the member has none. */
  roleName: string | null;
  /** All role names — a member may hold more than one. */
  roleNames: string[];
  alreadyAssigned: boolean;
}

export interface AssignableProvidersResult {
  currentCaseloadOwner: {
    memberId: string;
    userId: string;
    firstName: string;
    lastName: string;
  } | null;
  providers: AssignableProvider[];
}

export type CaseloadTransferPath = 'TRANSFER' | 'KEEP';

export interface CaseloadConflictPreview {
  hasOwner: boolean;
  currentOwner: { userId: string; firstName: string; lastName: string } | null;
  openRedDotCount: number;
}

export interface PendingCaseloadTransfer {
  id: string;
  newOwnerUserId: string;
  path: CaseloadTransferPath;
  effectiveDate: string;
  status: 'PENDING' | 'APPLIED' | 'CANCELLED' | 'SUPERSEDED';
}

export interface AssignProvidersToClientPayload {
  memberIds: string[];
  caseloadOwnerMemberId?: string;
  /** Resolution when the client already has a caseload owner. */
  conflictResolution?: CaseloadTransferPath;
  /** Transfer open Red Dots to the new owner (TRANSFER path only). Deprecated — use redDotDisposition. */
  transferOpenRedDots?: boolean;
  /** Disposition for open Red Dots when the caseload owner changes. Defaults to TRANSFER_TO_NEW. */
  redDotDisposition?: RedDotDisposition;
  /** Effective date (ISO YYYY-MM-DD). Omit or use today for immediate. */
  effectiveDate?: string;
}

export interface AssignProvidersToClientResult {
  created: number;
  skipped: number;
  ownerSet: boolean;
  scheduled?: boolean;
  effectiveDate?: string;
  redDots?: RedDotDispositionResult;
}

export interface SetOverseeingBcbaResult {
  scheduled: boolean;
  pendingTransferId?: string;
  redDots?: RedDotDispositionResult;
}

/** A person's access level on a given workflow — drives the non-blocking permission warning. */
export type WorkflowPermissionLevel = 'MANAGE' | 'EXECUTE' | 'NONE';

/** Per-workflow MANAGE/EXECUTE/NONE, keyed by workflow so more can be added without a type change. */
export interface WorkflowPermissions {
  redDot: WorkflowPermissionLevel;
  preference: WorkflowPermissionLevel;
}

/** Org-wide provider (no per-client flags) for the bulk assign/unassign drawer. */
export interface AssignableProviderLite {
  memberId: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  centerName: string | null;
  roleName: string | null;
  roleNames: string[];
  workflowPermissions: WorkflowPermissions;
}

export type BulkProviderAction = 'ASSIGN' | 'UNASSIGN';

export interface BulkUpdateClientProvidersPayload {
  clientIds: string[];
  providerMemberIds: string[];
  action: BulkProviderAction;
}

export interface BulkUpdateClientProvidersResult {
  action: BulkProviderAction;
  affected: number;
  skipped: number;
}

export interface BulkBcbaConflict {
  clientId: string;
  clientName: string;
  currentOwnerName: string;
}

export interface BulkBcbaPreviewResult {
  totalSelected: number;
  withOwner: BulkBcbaConflict[];
  withoutOwnerCount: number;
}

export type BcbaConflictResolution = 'REPLACE' | 'SKIP';

export interface BulkBcbaPayload {
  clientIds: string[];
  action: BulkProviderAction;
  memberId?: string;
  conflictResolution?: BcbaConflictResolution;
  transferOpenRedDots?: boolean;
}

export interface BulkBcbaResult {
  action: BulkProviderAction;
  assigned: number;
  skipped: number;
  cleared: number;
  redDotsTransferred: number;
}

/** Raw clinician row as returned by the API (nested member.user) before flattening. */
interface RawClientClinician {
  memberId: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  assignedAt: string;
  centerName?: string | null;
  roleName?: string | null;
  roleNames?: string[];
  member?: {
    userId?: string;
    user?: { id: string; firstName: string; lastName: string; email: string };
    primaryCenter?: { id?: string; name: string } | null;
    userRoles?: Array<{ role: { id?: string; name: string } }>;
  };
}

interface BulkClientValidateApiRow {
  rowNumber: number;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  gender?: string;
  centerName?: string;
  centerId?: string;
  status: 'valid' | 'invalid';
  reason?: string;
}

interface BulkClientValidateApiResponse {
  valid: BulkClientValidateApiRow[];
  invalid: BulkClientValidateApiRow[];
  validCount: number;
  invalidCount: number;
  totalRows: number;
}

const mapBulkClientRow = (row: BulkClientValidateApiRow): BulkClientValidateRow => ({
  row: row.rowNumber,
  firstName: row.firstName,
  lastName: row.lastName,
  dateOfBirth: row.dateOfBirth,
  gender: row.gender,
  center: row.centerName,
  centerId: row.centerId,
  valid: row.status === 'valid',
  reason: row.reason,
});

const clientService = {
  async getClients(params?: GetClientsParams): Promise<ClientListResponse> {
    const { data } = await apiClient.get('/api/v1/clients', { params });
    return data;
  },

  async getClient(id: string): Promise<Client> {
    const { data } = await apiClient.get(`/api/v1/clients/${id}`);
    return data.data;
  },

  async createClient(dto: CreateClientDto): Promise<Client> {
    const { data } = await apiClient.post('/api/v1/clients', dto);
    return data.data;
  },

  async updateClient(id: string, dto: UpdateClientDto): Promise<Client> {
    const { data } = await apiClient.patch(`/api/v1/clients/${id}`, dto);
    return data.data;
  },

  async deleteClient(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/clients/${id}`);
  },

  /** Change a single client's status (also how an off-roster client is reactivated). */
  async updateClientStatus(id: string, status: ClientStatus): Promise<Client> {
    const { data } = await apiClient.patch(`/api/v1/clients/${id}/status`, { status });
    return data.data;
  },

  /** Bulk-change status for many clients at once. */
  async bulkUpdateClientStatus(
    clientIds: string[],
    status: ClientStatus,
  ): Promise<BulkUpdateClientStatusResult> {
    const { data } = await apiClient.patch('/api/v1/clients/bulk/status', { clientIds, status });
    return data.data ?? data;
  },

  async getClientClinicians(clientId: string): Promise<ClientClinician[]> {
    const { data } = await apiClient.get(`/api/v1/clients/${clientId}/clinicians`);
    const rows = (data.data ?? data ?? []) as RawClientClinician[];
    // The API returns the clinician nested under member.user; the UI expects a flat
    // ClientClinician. Flatten here (tolerant of an already-flat shape).
    return rows.map((r) => {
      const user = r.member?.user;
      const roleNames = r.roleNames ?? r.member?.userRoles?.map((ur) => ur.role.name) ?? [];
      return {
        memberId: r.memberId,
        userId: user?.id ?? r.member?.userId ?? r.userId ?? '',
        firstName: user?.firstName ?? r.firstName ?? '',
        lastName: user?.lastName ?? r.lastName ?? '',
        email: user?.email ?? r.email ?? '',
        assignedAt: r.assignedAt,
        centerName: r.centerName ?? r.member?.primaryCenter?.name ?? null,
        roleName: r.roleName ?? roleNames[0] ?? null,
        roleNames,
      };
    });
  },

  async assignClinician(clientId: string, memberId: string): Promise<void> {
    await apiClient.post(`/api/v1/clients/${clientId}/clinicians`, { memberId });
  },

  async removeClinician(clientId: string, memberId: string): Promise<void> {
    await apiClient.delete(`/api/v1/clients/${clientId}/clinicians/${memberId}`);
  },

  /**
   * Validate a bulk client upload CSV file
   */
  async bulkValidate(file: File): Promise<BulkClientValidateResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await apiClient.post<{
      success: boolean;
      data: BulkClientValidateApiResponse;
    }>('/api/v1/clients/bulk/validate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const rows = [...data.data.valid, ...data.data.invalid]
      .sort((a, b) => a.rowNumber - b.rowNumber)
      .map(mapBulkClientRow);

    return {
      rows,
      validCount: data.data.validCount,
      invalidCount: data.data.invalidCount,
      totalCount: data.data.totalRows,
    };
  },

  /**
   * Get clients assigned to a specific member (for caseload transfer preview).
   * Uses the server-enforced cap of 100 per page; paginates through all pages
   * to build the complete caseload list.
   */
  async getClientsByAssignee(memberId: string): Promise<Client[]> {
    const PAGE_SIZE = 100;
    const firstPage = await apiClient.get<ClientListResponse>('/api/v1/clients', {
      params: { assigneeId: memberId, limit: PAGE_SIZE, page: 1 },
    });
    const allClients = [...firstPage.data.data];
    const { totalPages } = firstPage.data.meta;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiClient.get<ClientListResponse>('/api/v1/clients', {
            params: { assigneeId: memberId, limit: PAGE_SIZE, page: i + 2 },
          }),
        ),
      );
      rest.forEach((r) => allClients.push(...r.data.data));
    }
    return allClients;
  },

  /**
   * Bulk-reassign clients from one clinician to another
   */
  async transferCaseload(payload: TransferCaseloadRequest): Promise<TransferCaseloadResponse> {
    const { data } = await apiClient.post<{ success: boolean; data: TransferCaseloadResponse }>(
      '/api/v1/clients/transfer',
      payload,
    );
    return data.data;
  },

  /**
   * Preview the aggregate open-Red-Dot impact of a bulk caseload transfer before executing it.
   */
  async getTransferCaseloadPreview(
    fromMemberId: string,
    clientIds?: string[],
  ): Promise<TransferCaseloadPreviewResult> {
    const { data } = await apiClient.post('/api/v1/clients/transfer/preview', {
      fromMemberId,
      clientIds,
    });
    return data.data ?? data;
  },

  /**
   * Execute bulk client creation for pre-validated rows
   */
  async bulkExecute(rows: BulkClientValidateRow[]): Promise<BulkClientExecuteResponse> {
    const payloadRows = rows.map((row) => ({
      firstName: row.firstName ?? '',
      lastName: row.lastName ?? '',
      dateOfBirth: row.dateOfBirth,
      gender: row.gender,
      centerId: row.centerId,
      centerName: row.center,
    }));

    const { data } = await apiClient.post('/api/v1/clients/bulk/execute', { rows: payloadRows });
    return data.data;
  },

  /**
   * Fetch every active client in the org by paginating through all pages
   * (server caps page size at 100). Used by the Assignments admin screen.
   */
  async getAllClients(): Promise<Client[]> {
    const PAGE_SIZE = 100;
    const first = await apiClient.get<ClientListResponse>('/api/v1/clients', {
      params: { limit: PAGE_SIZE, page: 1 },
    });
    const all = [...first.data.data];
    const { totalPages } = first.data.meta;
    if (totalPages > 1) {
      const rest = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) =>
          apiClient.get<ClientListResponse>('/api/v1/clients', {
            params: { limit: PAGE_SIZE, page: i + 2 },
          }),
        ),
      );
      rest.forEach((r) => all.push(...r.data.data));
    }
    return all;
  },

  // ==================== Client–Provider Assignments + Coverage ====================

  /** Bulk-connect many clients to one provider (PROVIDER reach links). */
  async bulkAssignProviders(
    payload: BulkAssignProvidersPayload,
  ): Promise<BulkAssignProvidersResult> {
    const { data } = await apiClient.post('/api/v1/client-provider-assignments/bulk', payload);
    return data.data ?? data;
  },

  /** Set the single Overseeing BCBA (owner of record) for a client. */
  async setOverseeingBcba(
    clientId: string,
    memberId: string,
    effectiveFrom?: string,
    redDotDisposition?: RedDotDisposition,
  ): Promise<SetOverseeingBcbaResult> {
    const { data } = await apiClient.post(`/api/v1/clients/${clientId}/providers`, {
      memberId,
      effectiveFrom,
      redDotDisposition,
    });
    return data.data ?? data;
  },

  /** List active PROVIDER reach links for a client. */
  async listActiveProviders(clientId: string): Promise<ProviderAssignment[]> {
    const { data } = await apiClient.get(`/api/v1/clients/${clientId}/providers`);
    return data.data ?? data;
  },

  /** End a provider/coverage assignment (stamps effectiveTo = today). */
  async endProviderAssignment(clientId: string, assignmentId: string): Promise<void> {
    await apiClient.delete(`/api/v1/clients/${clientId}/providers/${assignmentId}`);
  },

  /** Set a time-boxed coverage window for a client. */
  async setCoverage(clientId: string, payload: SetCoveragePayload): Promise<CoverageWindow> {
    const { data } = await apiClient.post(
      `/api/v1/clients/${clientId}/providers/coverage`,
      payload,
    );
    return data.data ?? data;
  },

  /** List active coverage windows for a client (auto-reverts on read). */
  async listActiveCoverage(clientId: string): Promise<CoverageWindow[]> {
    const { data } = await apiClient.get(`/api/v1/clients/${clientId}/providers/coverage`);
    return data.data ?? data;
  },

  /** List providers assignable to a client (for the Assign flyout). */
  async getAssignableProviders(clientId: string): Promise<AssignableProvidersResult> {
    const { data } = await apiClient.get(`/api/v1/clients/${clientId}/providers/assignable`);
    return data.data ?? data;
  },

  /** Assign one or many providers to a client (optionally set the caseload owner). */
  async assignProvidersToClient(
    clientId: string,
    payload: AssignProvidersToClientPayload,
  ): Promise<AssignProvidersToClientResult> {
    const { data } = await apiClient.post(`/api/v1/clients/${clientId}/providers/assign`, payload);
    return data.data ?? data;
  },

  /** Org-wide list of providers for the bulk assign/unassign drawer. */
  async listAllAssignableProviders(): Promise<AssignableProviderLite[]> {
    const { data } = await apiClient.get(
      '/api/v1/client-provider-assignments/assignable-providers',
    );
    return data.data ?? data;
  },

  /** Bulk assign/unassign many providers to/from many clients in one call. */
  async bulkUpdateClientProviders(
    payload: BulkUpdateClientProvidersPayload,
  ): Promise<BulkUpdateClientProvidersResult> {
    const { data } = await apiClient.post(
      '/api/v1/client-provider-assignments/bulk-multi',
      payload,
    );
    return data.data ?? data;
  },

  /** Preview which selected clients already have an Overseeing BCBA. */
  async previewBulkBcba(clientIds: string[]): Promise<BulkBcbaPreviewResult> {
    const { data } = await apiClient.post('/api/v1/client-provider-assignments/bulk-bcba/preview', {
      clientIds,
    });
    return data.data ?? data;
  },

  /** Bulk set or clear the Overseeing BCBA for many clients. */
  async bulkUpdateBcba(payload: BulkBcbaPayload): Promise<BulkBcbaResult> {
    const { data } = await apiClient.post('/api/v1/client-provider-assignments/bulk-bcba', payload);
    return data.data ?? data;
  },

  /** Preview conflict data (current owner + open Red Dot count) for a caseload-owner change. */
  async getCaseloadConflict(
    clientId: string,
    newOwnerMemberId: string,
  ): Promise<CaseloadConflictPreview> {
    const { data } = await apiClient.get(
      `/api/v1/clients/${clientId}/providers/caseload-conflict`,
      { params: { newOwnerMemberId } },
    );
    return data.data ?? data;
  },

  /** Get the active pending caseload transfer for a client, or null if none. */
  async getPendingTransfer(clientId: string): Promise<PendingCaseloadTransfer | null> {
    const { data } = await apiClient.get(
      `/api/v1/clients/${clientId}/providers/pending-transfer`,
    );
    return data.data ?? data ?? null;
  },

  /** Cancel a pending caseload transfer by id. */
  async cancelPendingTransfer(clientId: string, id: string): Promise<{ cancelled: true }> {
    const { data } = await apiClient.delete(
      `/api/v1/clients/${clientId}/providers/pending-transfer/${id}`,
    );
    return data.data ?? data;
  },
};

export default clientService;
